import * as React from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import style from "./tenant-profile.scss";
import { ViewTenantDetails } from "components/customer-details/ViewTenantDetails";
import { SubpageLayout } from "components/layout/subpage-layout/SubpageLayout";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import { TENANT_PROFILE_ROUTE } from "components/router/Routes";
import { Tenant } from "domain/tenants";
import { getTenantUuid } from "services/tenants/tenantCookieService";
import { tenantService } from "services/tenants/TenantService";

enum ProfileState {
    LOADING = 0,
    LOADING_SUCCESS,
    LOADING_FAILED,
}

export const TenantProfile = (): JSX.Element => {
    const { t } = useTranslation();
    const { current: abortControllers } = React.useRef<AbortController[]>([]);
    const [tenantDetails, setTenantDetails] = React.useState<Tenant>();
    const [profileState, setProfileState] = React.useState(ProfileState.LOADING);
    const history = useHistory();

    const handleClose = React.useCallback(() => {
        if (history.length > 1) {
            history.goBack();
        } else {
            history.push("/");
        }
    }, [history]);

    React.useEffect(() => {
        const abortController = new AbortController();
        abortControllers.push(abortController);

        tenantService
            .fetchTenant(getTenantUuid(), abortController)
            .then((tenantDetails) => {
                setTenantDetails(tenantDetails);
                setProfileState(ProfileState.LOADING_SUCCESS);
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setProfileState(ProfileState.LOADING_FAILED);
                }
            });

        return () => {
            abortControllers.forEach((controller) => controller.abort());
        };
    }, []);

    if (profileState === ProfileState.LOADING) {
        return <LoadingIndicator />;
    }

    if (profileState === ProfileState.LOADING_FAILED) {
        return (
            <SubpageLayout
                visible={true}
                title={t(TENANT_PROFILE_ROUTE.title)}
                primaryButtonRequired={true}
                onClose={handleClose}
            >
                <div className={style.errorContainer}>
                    <div>{t("CustomerProfile.refreshRequestFailed")}</div>
                </div>
            </SubpageLayout>
        );
    }

    if (!tenantDetails) {
        return <></>;
    }

    return (
        <SubpageLayout
            visible={true}
            title={t(TENANT_PROFILE_ROUTE.title)}
            primaryButtonRequired={true}
            onClose={handleClose}
        >
            <ViewTenantDetails activeTenant={tenantDetails} />
        </SubpageLayout>
    );
};
